
import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../services/apiClient";
import { appConfig } from "../api";
import hotelLogo from "../assets/img/elite-logo.png";
import {
  formatDisplayDate,
  getDefaultBookingFilterRange,
} from "../utils/dateUtils";

const STAY_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "arriving_today", label: "Arriving Today" },
  { value: "departing_today", label: "Departing Today" },
  { value: "pending_checkin", label: "Pending Check-in" },
  { value: "checked_in", label: "In House" },
  { value: "checked_out", label: "Checked Out" },
  { value: "cancelled", label: "Cancelled" },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "successful", label: "Paid" },
];

const SORT_OPTIONS = [
  { value: "latest", label: "Latest booking" },
  { value: "oldest", label: "Oldest booking" },
  { value: "checkInAsc", label: "Check-in (earliest)" },
  { value: "checkInDesc", label: "Check-in (latest)" },
];

const DATE_TYPE_OPTIONS = [
  { value: "checkIn", label: "Check-in" },
  { value: "checkOut", label: "Check-out" },
];

const getDefaultFilters = () => {
  const { fromDate, toDate } = getDefaultBookingFilterRange(1);
  return {
    dateType: "checkIn",
    fromDate,
    toDate,
    stayStatus: "all",
    paymentStatus: "all",
    sort: "latest",
  };
};

const filterInputClass =
  "w-full border border-gray-300 rounded-lg py-2 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-yellow-400";

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [summary, setSummary] = useState({
    loadedBookings: 0,
    todayArrivals: 0,
    inHouse: 0,
    pendingPayments: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState(getDefaultFilters);
  const [toast, setToast] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const pageSize = appConfig.bookingsPageSize;

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/bookings", {
        params: {
          page,
          limit: pageSize,
          searchQuery,
          ...filters,
        },
      });

      const data = response.data;
      const fetchedBookings = data.bookings ?? data;

      setBookings((prev) =>
        page === 1 ? fetchedBookings : [...prev, ...fetchedBookings]
      );
      setSummary(
        data.summary ?? {
          loadedBookings: data.total ?? fetchedBookings.length,
          todayArrivals: 0,
          inHouse: 0,
          pendingPayments: 0,
        }
      );
      setTotalPages(data.totalPages ?? 1);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      showToast("Failed to load bookings. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  }, [page, filters, searchQuery, pageSize]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
    setBookings([]);
  };

  const handleSearch = () => {
    setPage(1);
    setBookings([]);
    fetchBookings();
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleReset = () => {
    setSearchQuery("");
    setFilters(getDefaultFilters());
    setPage(1);
    setBookings([]);
  };

  const updateBookingLocally = (id, updates) => {
    setBookings((prev) =>
      prev.map((b) => (b.id === id ? { ...b, ...updates } : b))
    );
  };

  const handleCheckIn = async (id, bookingId) => {
    setActionLoading(bookingId);
    try {
      await apiClient.patch(`/bookings/update-status/${bookingId}`, {
        isCheckedIn: true,
      });
      updateBookingLocally(id, { isCheckedIn: true });
      showToast(`Guest checked in — ${bookingId}`);
      fetchBookings();
    } catch (error) {
      showToast("Check-in failed. Please try again.", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCheckOut = async (id, bookingId) => {
    setActionLoading(bookingId);
    try {
      await apiClient.patch(`/bookings/update-status/${bookingId}`, {
        isCheckedOut: true,
      });
      updateBookingLocally(id, { isCheckedOut: true });
      showToast(`Guest checked out — ${bookingId}`);
      fetchBookings();
    } catch (error) {
      showToast("Check-out failed. Please try again.", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkPaid = async (id, bookingId) => {
    setActionLoading(`${bookingId}-pay`);
    try {
      await apiClient.patch(`/bookings/update-status/${bookingId}`, {
        paymentStatus: "successful",
      });
      updateBookingLocally(id, { paymentStatus: "successful" });
      showToast(`Payment marked as received — ${bookingId}`);
      fetchBookings();
    } catch (error) {
      showToast("Failed to update payment status.", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const navigateToInvoice = (booking) => {
    const invoiceKey = `${booking.bookingId || booking.id}-${Date.now()}`;
    window.localStorage.setItem(`invoice:${invoiceKey}`, JSON.stringify(booking));
    window.open(`/invoice?key=${encodeURIComponent(invoiceKey)}&print=1`, "_blank", "noopener,noreferrer");
  };

  const navigateToGRC = (booking) => {
    const grcKey = `${booking.bookingId || booking.id}-${Date.now()}`;
    window.localStorage.setItem(`grc:${grcKey}`, JSON.stringify(booking));
    window.open(`/grc?key=${encodeURIComponent(grcKey)}&print=1`, "_blank", "noopener,noreferrer");
  };

  const getStayBadge = (booking) => {
    if (booking.status === "Cancelled") return { label: "Cancelled", className: "bg-red-100 text-red-700" };
    if (booking.isCheckedOut) return { label: "Checked Out", className: "bg-gray-100 text-gray-700" };
    if (booking.isCheckedIn) return { label: "In House", className: "bg-green-100 text-green-700" };
    return { label: "Pending Check-in", className: "bg-amber-100 text-amber-800" };
  };

  const getPaymentLabel = (booking) => {
    if (booking.paymentStatus === "successful") return { label: "Paid", className: "text-green-600" };
    if (booking.paymentStatus === "pending") return { label: "Pending", className: "text-amber-600" };
    return { label: booking.paymentStatus || "N/A", className: "text-gray-500" };
  };

  const hasMore = page < totalPages;

  const summaryCards = [
    { label: "Loaded Bookings", value: summary.loadedBookings },
    { label: "Today Arrivals", value: summary.todayArrivals },
    { label: "In House", value: summary.inHouse },
    { label: "Pending Payments", value: summary.pendingPayments },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-lg shadow-lg text-sm font-medium ${
            toast.type === "error" ? "bg-red-600 text-white" : "bg-green-600 text-white"
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Header + search */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs font-semibold tracking-widest text-amber-600 uppercase">Front Desk</p>
          <h1 className="text-3xl font-bold text-gray-900 font-primary">Bookings</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Showing the next month by default, with newest bookings first.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <input
            type="text"
            placeholder="Booking ID, guest, phone, room..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="w-64 lg:w-80 border border-gray-300 rounded-lg py-2 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
          <button
            type="button"
            onClick={handleSearch}
            className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 px-4 py-2 rounded-lg text-sm font-semibold"
          >
            Search
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5M5.07 17.93A10 10 0 1121 12" />
            </svg>
            Reset
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {summaryCards.map((card) => (
          <div key={card.label} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <p className="text-sm text-gray-500">{card.label}</p>
            <p className="text-4xl font-bold text-gray-900 mt-2">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Filters row */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Date Type</label>
            <select name="dateType" value={filters.dateType} onChange={handleFilterChange} className={filterInputClass}>
              {DATE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">From</label>
            <input type="date" name="fromDate" value={filters.fromDate} onChange={handleFilterChange} className={filterInputClass} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">To</label>
            <input type="date" name="toDate" value={filters.toDate} onChange={handleFilterChange} className={filterInputClass} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Payment</label>
            <select name="paymentStatus" value={filters.paymentStatus} onChange={handleFilterChange} className={filterInputClass}>
              {PAYMENT_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Booking Status</label>
            <select name="stayStatus" value={filters.stayStatus} onChange={handleFilterChange} className={filterInputClass}>
              {STAY_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Sort</label>
            <select name="sort" value={filters.sort} onChange={handleFilterChange} className={filterInputClass}>
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Bookings table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm bg-white">
        <table className="min-w-full">
          <thead>
            <tr className="bg-slate-900 text-white">
              <th className="p-4 text-left text-sm font-semibold">Guest</th>
              <th className="p-4 text-left text-sm font-semibold">Stay</th>
              <th className="p-4 text-left text-sm font-semibold">Room</th>
              <th className="p-4 text-left text-sm font-semibold">Booking ID</th>
              <th className="p-4 text-left text-sm font-semibold">Amount</th>
              <th className="p-4 text-left text-sm font-semibold">Status</th>
              <th className="p-4 text-left text-sm font-semibold min-w-[180px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && page === 1 ? (
              <tr>
                <td colSpan={7} className="p-12 text-center text-gray-500">
                  Loading bookings...
                </td>
              </tr>
            ) : bookings.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-12 text-center text-gray-500">
                  No bookings found for these filters.
                </td>
              </tr>
            ) : (
              bookings.map((booking, index) => {
                const stayBadge = getStayBadge(booking);
                const paymentBadge = getPaymentLabel(booking);
                const isCancelled = booking.status === "Cancelled";
                const isBusy =
                  actionLoading === booking.bookingId ||
                  actionLoading === `${booking.bookingId}-pay`;

                return (
                  <tr
                    key={booking.id}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="p-4 border-t border-gray-100">
                      <p className="font-semibold text-gray-900">{booking.customerName}</p>
                      <p className="text-sm text-gray-500 mt-0.5">{booking.phoneNumber}</p>
                    </td>
                    <td className="p-4 border-t border-gray-100 text-sm text-gray-700">
                      <p>In: {formatDisplayDate(booking.checkInDate)}</p>
                      <p className="mt-0.5">Out: {formatDisplayDate(booking.checkOutDate)}</p>
                    </td>
                    <td className="p-4 border-t border-gray-100 text-sm text-gray-700">
                      <p className="font-medium text-gray-900">{booking.roomType}</p>
                      <p className="text-gray-500 mt-0.5">
                        {booking.adults} adult{booking.adults !== 1 ? "s" : ""}, {booking.kids} kid{booking.kids !== 1 ? "s" : ""}
                      </p>
                    </td>
                    <td className="p-4 border-t border-gray-100">
                      <p className="text-sm font-mono text-gray-800">{booking.bookingId || "N/A"}</p>
                    </td>
                    <td className="p-4 border-t border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">
                        ₹{Number(booking.amount || 0).toLocaleString("en-IN")}
                      </p>
                    </td>
                    <td className="p-4 border-t border-gray-100">
                      <span className={`inline-block text-xs px-2.5 py-1 rounded-full font-medium ${stayBadge.className}`}>
                        {stayBadge.label}
                      </span>
                      <p className={`text-xs mt-1.5 font-medium ${paymentBadge.className}`}>
                        {paymentBadge.label}
                      </p>
                    </td>
                    <td className="p-4 border-t border-gray-100">
                      {!isCancelled ? (
                        <div className="flex flex-wrap gap-1.5">
                          <button
                            type="button"
                            className="text-xs px-2.5 py-1.5 rounded-md bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50"
                            onClick={() => navigateToInvoice(booking)}
                          >
                            Invoice
                          </button>
                          <button
                            type="button"
                            className="text-xs px-2.5 py-1.5 rounded-md bg-teal-700 text-white hover:bg-teal-600 disabled:opacity-50 font-medium"
                            onClick={() => navigateToGRC(booking)}
                          >
                            GRC
                          </button>
                          {!booking.isCheckedIn && (
                            <button
                              type="button"
                              onClick={() => handleCheckIn(booking.id, booking.bookingId)}
                              disabled={isBusy}
                              className="text-xs px-2.5 py-1.5 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                            >
                              Check-In
                            </button>
                          )}
                          {booking.isCheckedIn && !booking.isCheckedOut && (
                            <button
                              type="button"
                              onClick={() => handleCheckOut(booking.id, booking.bookingId)}
                              disabled={isBusy}
                              className="text-xs px-2.5 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                              Check-Out
                            </button>
                          )}
                          {booking.paymentStatus === "pending" && (
                            <button
                              type="button"
                              onClick={() => handleMarkPaid(booking.id, booking.bookingId)}
                              disabled={isBusy}
                              className="text-xs px-2.5 py-1.5 rounded-md bg-amber-500 text-gray-900 hover:bg-amber-600 disabled:opacity-50"
                            >
                              Mark Paid
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="flex justify-center mt-6">
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={loading}
            className="bg-yellow-500 text-gray-900 px-6 py-2 rounded-lg hover:bg-yellow-600 disabled:opacity-50 text-sm font-semibold"
          >
            {loading ? "Loading..." : `Load More (${page}/${totalPages})`}
          </button>
        </div>
      )}
    </div>
  );
};

export default Bookings;
